import { useState, type ReactNode } from "react";
import { useRevealSearchTarget, type SearchFocus } from "@/shared/hooks/useSearchFocus";
import { Icon } from "@iconify/react";
import { EmptyState } from "@/shared/components/EmptyState";
import { PillTabs } from "@/shared/components/PillTabs";
import { UnderlineTabs } from "@/shared/components/UnderlineTabs";
import { FoundInRecordsTag } from "@/shared/components/FoundInRecords";
import { GoldenSources, GoldenTag } from "@/features/empi/GoldenRecord";
import { cn } from "@/shared/lib/utils";
import type {
  AllergyRecord,
  AllergySeverity,
  DiagnosisRecord,
  ExamVitalItem,
  ExamVitalsVisit,
  ImmunizationRecord,
  ProcedureRecord,
} from "@/features/patient/clinical-history/utils/adaptClinicalHistory";
import type { RecordDuplicates } from "@/features/patient/records/api/dataPipelineApi";
import type { EmpiRecordGolden } from "@/shared/types/empi";

/**
 * Complete History, Medical tab: underline tabs (Allergies / Conditions /
 * Procedures / Immunizations / Exam & Vitals) over one shared card design
 * (RecordCard): a coloured icon tile, the title with its status on the right,
 * labelled facts, who recorded it, and where it came from in the footer.
 * Columns grow with the screen. A field shows only when the record has it:
 * no "Not available" filler. Where each one came from stays one click away.
 */

type MedicalTab = "allergies" | "conditions" | "procedures" | "immunizations" | "examVitals";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatDate(value: string): string {
  if (!value) return "";
  const date = new Date(value + "T00:00:00");
  return Number.isNaN(date.getTime()) ? "" : `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

/** "Recorded by Dr. X · Lakeshore Internal Medicine": only the parts the record has. */
function WhoAndWhere({ verb, who, where }: { verb: string; who: string; where: string }) {
  if (!who && !where) return null;
  return (
    <p className="!mb-0 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-[#6B7280]">
      <Icon icon="lucide:user-round" className="h-3.5 w-3.5 shrink-0 text-[#9CA3AF]" />
      {who && <span>{verb} <span className="font-semibold text-[#111827]">{who}</span></span>}
      {who && where && <span aria-hidden>·</span>}
      {where && <span>{where}</span>}
    </p>
  );
}

function Provenance({ golden, duplicates }: { golden?: EmpiRecordGolden | null; duplicates?: RecordDuplicates | null }) {
  if (golden) {
    return (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <GoldenTag golden={golden} />
        <GoldenSources golden={golden} compact className="basis-full" />
      </div>
    );
  }
  return duplicates ? <FoundInRecordsTag duplicates={duplicates} /> : null;
}

// ── Shared card pieces (every tab uses the same card) ─────────────────────────

type Tone = "red" | "amber" | "navy" | "green" | "grey";

const TONE: Record<Tone, string> = {
  red: "bg-[#FFF1F3] text-[#E4002B]",
  amber: "bg-[#FEF6DC] text-[#B45309]",
  navy: "bg-[#EEF2F7] text-[#0A2947]",
  green: "bg-[#ECFDF3] text-[#027A48]",
  grey: "bg-[#F3F4F6] text-[#6B7280]",
};

/**
 * Columns that grow with the room the cards actually have (a container query
 * on the Medical section, so the sidebar is accounted for): 1 column on
 * phones, 2 from ~560px, 3 from ~880px, 4 from ~1400px, 5 from ~2000px.
 */
const CARD_GRID = "grid gap-4 @[560px]:grid-cols-2 @[880px]:grid-cols-3 @[1400px]:grid-cols-4 @[2000px]:grid-cols-5";

function CodeChip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex shrink-0 rounded-md bg-[#F3F4F6] px-1.5 py-0.5 font-mono text-[10px] font-semibold text-[#4B5563]">{children}</span>
  );
}

function StatusPill({ tone, dot, children }: { tone: Tone; dot?: boolean; children: ReactNode }) {
  return (
    <span className={cn("inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-bold", TONE[tone])}>
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}

/** Small grey caps label with the value under it; a missing value isn't shown (unless it has a placeholder). */
function Facts({ items }: { items: { label: string; value: ReactNode; placeholder?: string }[] }) {
  const shown = items.filter((i) => i.value || i.placeholder);
  if (shown.length === 0) return null;
  return (
    <dl className="!mb-0 grid grid-cols-2 gap-x-4 gap-y-3">
      {shown.map((item) => (
        <div key={item.label} className="min-w-0">
          <dt className="text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF]">{item.label}</dt>
          <dd className={cn("!mb-0 !ml-0 !mt-1 break-words text-[13px]", item.value ? "font-semibold text-[#111827]" : "italic text-[#9CA3AF]")}>
            {item.value || item.placeholder}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * The one card every Medical tab uses: icon tile + title (+ subtitle / code)
 * with its status on the right, then the facts, who recorded it, a note, and
 * where it came from in the footer. Lifts a little on hover.
 */
function RecordCard({
  testId, searchId, icon, tone, title, subtitle, code, status, children, footer,
}: {
  testId: string;
  /** The record's id, for the header search's "open this record" (useSearchFocus.ts). */
  searchId?: string;
  icon: string;
  tone: Tone;
  title: string;
  subtitle?: ReactNode;
  code?: ReactNode;
  status?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <article
      data-testid={testId}
      data-search-id={searchId}
      className="flex min-w-0 flex-col rounded-2xl border border-[#E5E7EB] bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
    >
      <header className="flex items-start gap-3">
        <span className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl", TONE[tone])}>
          <Icon icon={icon} className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="!mb-0 line-clamp-2 text-[15px] !font-bold leading-5 text-[#0A2947] [overflow-wrap:anywhere]" title={title}>{title}</h3>
          {(subtitle || code) && (
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[#6B7280]">
              {subtitle && <span>{subtitle}</span>}
              {code}
            </div>
          )}
        </div>
        {status}
      </header>
      {children && <div className="mt-4 grid gap-3">{children}</div>}
      {footer && <footer className="mt-auto pt-4"><div className="border-t border-[#F0F1F3] pt-3">{footer}</div></footer>}
    </article>
  );
}

/** A line of totals over a tab's cards. */
function Summary({ children }: { children: ReactNode }) {
  return <p className="!mb-0 text-sm text-[#4B5563]">{children}</p>;
}

// ── Allergies ─────────────────────────────────────────────────────────────────

const SEVERITY: Record<AllergySeverity, { label: string; tone: Tone }> = {
  high: { label: "High severity", tone: "red" },
  moderate: { label: "Moderate", tone: "amber" },
  low: { label: "Low severity", tone: "grey" },
  unknown: { label: "Severity not recorded", tone: "grey" },
};

const CATEGORY_ICON: Record<string, string> = {
  medication: "lucide:pill",
  food: "lucide:apple",
  environment: "lucide:leaf",
  biologic: "lucide:syringe",
};

function categoryLabel(category: string): string {
  return category ? category.charAt(0).toUpperCase() + category.slice(1) : "";
}

function AllergyCard({ allergy }: { allergy: AllergyRecord }) {
  const severity = SEVERITY[allergy.severity];
  const inactive = allergy.status === "inactive" || allergy.status === "resolved";
  const kind = allergy.category ? `${categoryLabel(allergy.category)} allergy` : "Allergy";
  return (
    <RecordCard
      testId="allergy-card"
      searchId={allergy.id}
      icon={CATEGORY_ICON[allergy.category] ?? "lucide:triangle-alert"}
      tone={allergy.severity === "high" ? "red" : allergy.severity === "moderate" ? "amber" : "navy"}
      title={allergy.name}
      subtitle={inactive ? `${kind} · No longer active` : kind}
      status={<StatusPill tone={severity.tone} dot>{severity.label}</StatusPill>}
      footer={(allergy.golden || allergy.duplicates) && <Provenance golden={allergy.golden} duplicates={allergy.duplicates} />}
    >
      <Facts
        items={[
          { label: "Reaction", value: allergy.reaction, placeholder: "Not recorded" },
          { label: "First noted", value: formatDate(allergy.notedDate) },
        ]}
      />
      <WhoAndWhere verb="Recorded by" who={allergy.recordedBy} where={allergy.location} />
    </RecordCard>
  );
}

function AllergiesPanel({ allergies }: { allergies: AllergyRecord[] }) {
  if (allergies.length === 0) {
    return (
      <EmptyState
        icon={<Icon icon="lucide:shield-check" className="h-12 w-12 text-[#9CA3AF]" />}
        title="No allergies on record"
        description="Allergies from your uploaded records will appear here."
      />
    );
  }
  const high = allergies.filter((a) => a.severity === "high").length;
  return (
    <div className="grid gap-4">
      <Summary>
        {allergies.length} {allergies.length === 1 ? "allergy" : "allergies"} on record
        {high > 0 && <span className="font-bold text-[#C8102E]"> · {high} high severity</span>}
      </Summary>
      <div className={CARD_GRID}>
        {allergies.map((allergy) => (
          <AllergyCard key={allergy.id} allergy={allergy} />
        ))}
      </div>
    </div>
  );
}

// ── Conditions ────────────────────────────────────────────────────────────────

type ConditionFilter = "active" | "resolved" | "all";

const CONDITION_STATUS: Record<DiagnosisRecord["status"], { label: string; tone: Tone }> = {
  active: { label: "Active", tone: "green" },
  chronic: { label: "Chronic", tone: "navy" },
  resolved: { label: "Resolved", tone: "grey" },
};

function ConditionCard({ condition }: { condition: DiagnosisRecord }) {
  const status = CONDITION_STATUS[condition.status];
  const code = condition.icdCode ? `ICD-10 ${condition.icdCode}` : condition.snomedCode ? `SNOMED ${condition.snomedCode}` : "";
  return (
    <RecordCard
      testId="condition-row"
      icon={condition.status === "resolved" ? "lucide:circle-check" : "lucide:stethoscope"}
      tone={condition.status === "resolved" ? "grey" : "navy"}
      title={condition.name}
      code={code ? <CodeChip>{code}</CodeChip> : undefined}
      status={<StatusPill tone={status.tone} dot>{status.label}</StatusPill>}
      footer={(condition.golden || condition.duplicates) && <Provenance golden={condition.golden} duplicates={condition.duplicates} />}
    >
      <Facts items={[{ label: "Since", value: formatDate(condition.diagnosedDate) }]} />
      {condition.description && (
        <p className="!mb-0 line-clamp-3 text-xs leading-5 text-[#4B5563]" title={condition.description}>{condition.description}</p>
      )}
      <WhoAndWhere verb="Recorded by" who={condition.diagnosedBy} where={condition.location} />
    </RecordCard>
  );
}

function ConditionsPanel({ conditions }: { conditions: DiagnosisRecord[] }) {
  const counts = {
    active: conditions.filter((c) => c.status !== "resolved").length,
    resolved: conditions.filter((c) => c.status === "resolved").length,
    all: conditions.length,
  };
  // Opens on what the patient has now; resolved ones are one click away.
  const [filter, setFilter] = useState<ConditionFilter>(counts.active > 0 ? "active" : "all");
  if (conditions.length === 0) {
    return (
      <EmptyState
        icon={<Icon icon="lucide:stethoscope" className="h-12 w-12 text-[#9CA3AF]" />}
        title="No conditions on record"
        description="Diagnoses and health problems from your uploaded records will appear here."
      />
    );
  }
  const shown = conditions.filter((c) =>
    filter === "all" ? true : filter === "resolved" ? c.status === "resolved" : c.status !== "resolved",
  );
  const FILTERS: { id: ConditionFilter; label: string }[] = [
    { id: "active", label: "Current" },
    { id: "resolved", label: "Resolved" },
    { id: "all", label: "All" },
  ];
  return (
    <div className="grid gap-4">
      <PillTabs<ConditionFilter>
        label="Filter conditions"
        active={filter}
        onChange={setFilter}
        className="self-start justify-self-start"
        tabs={FILTERS.map(({ id, label }) => ({ id, label: `${label} (${counts[id]})` }))}
      />
      {shown.length === 0 ? (
        <EmptyState
          icon={<Icon icon="lucide:stethoscope" className="h-8 w-8 text-[#9CA3AF]" />}
          title={`No ${filter === "resolved" ? "resolved" : "current"} conditions`}
          className="py-10"
        />
      ) : (
        <div className={CARD_GRID}>
          {shown.map((condition) => (
            <ConditionCard key={condition.id} condition={condition} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Procedures ────────────────────────────────────────────────────────────────

function ProceduresPanel({ procedures }: { procedures: ProcedureRecord[] }) {
  if (procedures.length === 0) {
    return (
      <EmptyState
        icon={<Icon icon="lucide:clipboard-list" className="h-12 w-12 text-[#9CA3AF]" />}
        title="No procedures on record"
        description="Tests, scans and surgeries from your uploaded records will appear here."
      />
    );
  }
  // Grouped by year, newest first; undated ones last.
  const byYear = new Map<string, ProcedureRecord[]>();
  for (const procedure of procedures) {
    const year = procedure.date ? procedure.date.slice(0, 4) : "Date not recorded";
    byYear.set(year, [...(byYear.get(year) ?? []), procedure]);
  }
  const years = [...byYear.keys()].sort((a, b) => (a === "Date not recorded" ? 1 : b === "Date not recorded" ? -1 : b.localeCompare(a)));
  return (
    <div className="grid gap-6">
      <Summary>
        {procedures.length} {procedures.length === 1 ? "procedure" : "procedures"} on record
      </Summary>
      {years.map((year) => (
        <section key={year} aria-label={year} className="grid gap-3">
          {/* Year divider: the label, then a thin line to the edge. */}
          <div className="flex items-center gap-3">
            <h3 className="!mb-0 text-xs !font-extrabold uppercase tracking-wider text-[#6B7280]">{year}</h3>
            <span className="h-px flex-1 bg-[#E5E7EB]" />
          </div>
          <div className={CARD_GRID}>
            {byYear.get(year)!.map((procedure) => (
              <RecordCard
                key={procedure.id}
                testId="procedure-item"
                searchId={procedure.id}
                icon="lucide:clipboard-plus"
                tone="navy"
                title={procedure.name}
                code={procedure.code ? <CodeChip>CPT {procedure.code}</CodeChip> : undefined}
                status={<StatusPill tone="grey">{formatDate(procedure.date) || "Date not recorded"}</StatusPill>}
                footer={(procedure.golden || procedure.duplicates) && <Provenance golden={procedure.golden} duplicates={procedure.duplicates} />}
              >
                <Facts items={[{ label: "Reason", value: procedure.reason }]} />
                <WhoAndWhere verb="Performed by" who={procedure.performedBy} where={procedure.location} />
              </RecordCard>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

// ── Immunizations ─────────────────────────────────────────────────────────────

const NOT_GIVEN_STATUS: Record<string, string> = {
  "not-done": "Not given",
  "entered-in-error": "Entered in error",
};

function ImmunizationsPanel({ immunizations }: { immunizations: ImmunizationRecord[] }) {
  if (immunizations.length === 0) {
    return (
      <EmptyState
        icon={<Icon icon="lucide:syringe" className="h-12 w-12 text-[#9CA3AF]" />}
        title="No immunizations on record"
        description="Vaccines from your uploaded records will appear here."
      />
    );
  }
  const latest = immunizations.find((i) => i.date && !NOT_GIVEN_STATUS[i.status]);
  return (
    <div className="grid gap-4">
      <Summary>
        {immunizations.length} {immunizations.length === 1 ? "dose" : "doses"} on record
        {latest && <span> · most recent {formatDate(latest.date)}</span>}
      </Summary>
      <div className={CARD_GRID}>
        {immunizations.map((dose) => {
          const notGiven = NOT_GIVEN_STATUS[dose.status];
          return (
            <RecordCard
              key={dose.id}
              testId="immunization-row"
              searchId={dose.id}
              icon="lucide:syringe"
              tone={notGiven ? "grey" : "green"}
              title={dose.name}
              code={dose.cvxCode ? <CodeChip>CVX {dose.cvxCode}</CodeChip> : undefined}
              status={
                notGiven ? (
                  <StatusPill tone="grey">{notGiven}</StatusPill>
                ) : (
                  <StatusPill tone="green" dot>{formatDate(dose.date) || "Date not recorded"}</StatusPill>
                )
              }
              footer={(dose.golden || dose.duplicates) && <Provenance golden={dose.golden} duplicates={dose.duplicates} />}
            >
              <Facts
                items={[
                  { label: "Dose", value: dose.dose },
                  { label: "Site", value: dose.site },
                  { label: "Route", value: dose.route },
                  { label: "Lot", value: dose.lotNumber },
                  ...(notGiven ? [{ label: "Date", value: formatDate(dose.date) }] : []),
                ]}
              />
              <WhoAndWhere verb="Given by" who={dose.givenBy} where={dose.location} />
              {dose.note && <p className="!mb-0 text-xs leading-5 text-[#4B5563]">{dose.note}</p>}
            </RecordCard>
          );
        })}
      </div>
    </div>
  );
}

// ── Exam & Vitals ─────────────────────────────────────────────────────────────

/** Vital signs as small tiles: the name small and grey, the value large. */
function VitalTiles({ items }: { items: ExamVitalItem[] }) {
  if (items.length === 0) return null;
  return (
    <div>
      <p className="!mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#6B7280]">
        <Icon icon="lucide:activity" className="h-3.5 w-3.5" /> Vital signs
      </p>
      <ul className="!mb-0 grid gap-2 !pl-0 [grid-template-columns:repeat(auto-fill,minmax(150px,1fr))]">
        {items.map((item) => (
          <li key={item.id} data-testid="exam-vital-item" className="flex flex-col rounded-xl bg-[#F9FAFB] px-3 py-2.5">
            <span className="text-xs text-[#6B7280]">{item.name}</span>
            {item.value && <span className="mt-0.5 text-[15px] font-bold text-[#0A2947]">{item.value}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Exam findings / other observations as a simple dotted list (value on the right when there is one). */
function ExamVitalsList({ title, icon, items }: { title: string; icon: string; items: ExamVitalItem[] }) {
  if (items.length === 0) return null;
  return (
    <div>
      <p className="!mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#6B7280]">
        <Icon icon={icon} className="h-3.5 w-3.5" /> {title}
      </p>
      <ul className="!mb-0 grid gap-1.5 !pl-0">
        {items.map((item) => (
          <li key={item.id} data-testid="exam-vital-item" className="flex items-start gap-2 text-sm">
            <span aria-hidden className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#F17893]" />
            {/* The value right after its name ("Tobacco use: Never smoker"), not pushed to the far edge. */}
            <span className="min-w-0 text-[#111827]">
              {item.value ? `${item.name}: ` : item.name}
              {item.value && <span className="font-semibold text-[#0A2947]">{item.value}</span>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** One visit day's vital signs, exam findings and other observations — shared
 *  by Complete History → Exam & Vitals and the Health Timeline. */
export function ExamVitalsVisitBody({ visit }: { visit: ExamVitalsVisit }) {
  return (
    <div className="grid gap-5">
      <VitalTiles items={visit.vitals} />
      {(visit.exam.length > 0 || visit.other.length > 0) && (
        <div className="grid gap-5 md:grid-cols-2">
          <ExamVitalsList title="Exam findings" icon="lucide:stethoscope" items={visit.exam} />
          <ExamVitalsList title="Other observations" icon="lucide:clipboard-list" items={visit.other} />
        </div>
      )}
    </div>
  );
}

function ExamVitalsPanel({ visits }: { visits: ExamVitalsVisit[] }) {
  if (visits.length === 0) {
    return (
      <EmptyState
        icon={<Icon icon="lucide:heart-pulse" className="h-12 w-12 text-[#9CA3AF]" />}
        title="No exam findings or vitals on record"
        description="Physical exam findings and vital signs from your records will appear here."
      />
    );
  }
  return (
    <div className="grid gap-4">
      <Summary>
        {visits.length} {visits.length === 1 ? "visit" : "visits"} with exam findings or vital signs
      </Summary>
      <div className="grid gap-4 @[1200px]:grid-cols-2">
        {visits.map((visit) => {
          const parts = [
            visit.vitals.length ? `${visit.vitals.length} vital ${visit.vitals.length === 1 ? "sign" : "signs"}` : "",
            visit.exam.length ? `${visit.exam.length} exam ${visit.exam.length === 1 ? "finding" : "findings"}` : "",
            visit.other.length ? `${visit.other.length} other` : "",
          ].filter(Boolean);
          return (
            <section
              key={visit.date || "undated"}
              data-testid="exam-vitals-visit"
              className="rounded-2xl border border-[#E5E7EB] bg-white p-5 shadow-sm transition-shadow duration-200 hover:shadow-md"
            >
              <header className="mb-4 flex items-center gap-3">
                <span className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl", TONE.navy)}>
                  <Icon icon="lucide:calendar-heart" className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <h3 className="!mb-0 text-[15px] !font-bold text-[#0A2947]">{formatDate(visit.date) || "Date not recorded"}</h3>
                  <p className="!mb-0 !mt-0.5 text-xs text-[#6B7280]">{parts.join(" · ")}</p>
                </div>
              </header>
              <ExamVitalsVisitBody visit={visit} />
            </section>
          );
        })}
      </div>
    </div>
  );
}

// ── The tab ───────────────────────────────────────────────────────────────────

export function MedicalHistoryPanels({
  allergies,
  conditions,
  procedures,
  immunizations = [],
  examAndVitals = [],
  focus,
}: {
  allergies: AllergyRecord[];
  conditions: DiagnosisRecord[];
  procedures: ProcedureRecord[];
  immunizations?: ImmunizationRecord[];
  examAndVitals?: ExamVitalsVisit[];
  /** A header search result: an allergy, procedure or vaccine dose to open. */
  focus?: SearchFocus;
}) {
  const counts: Record<MedicalTab, number> = {
    allergies: allergies.length,
    conditions: conditions.length,
    procedures: procedures.length,
    immunizations: immunizations.length,
    examVitals: examAndVitals.reduce((n, v) => n + v.vitals.length + v.exam.length + v.other.length, 0),
  };
  // Allergies first (they matter most for safety); otherwise the first list with anything in it.
  const firstWithData = (["allergies", "conditions", "procedures", "immunizations"] as MedicalTab[]).find((t) => counts[t] > 0);
  const [picked, setPicked] = useState<MedicalTab | null>(null);
  const active = picked ?? firstWithData ?? "allergies";
  // A header search result: the tab that holds it.
  const tabOf = (id: string): MedicalTab | null =>
    allergies.some((a) => a.id === id) ? "allergies"
      : procedures.some((p) => p.id === id) ? "procedures"
        : immunizations.some((i) => i.id === id) ? "immunizations"
          : null;
  useRevealSearchTarget(focus, (id) => tabOf(id) !== null, (id) => setPicked(tabOf(id)));

  return (
    <section className="@container grid gap-5">
      {/* Same underline tabs as Health Concerns' status filter. */}
      <UnderlineTabs<MedicalTab>
        label="Medical history"
        active={active}
        onChange={setPicked}
        tabs={[
          { id: "allergies", label: "Allergies", count: counts.allergies },
          { id: "conditions", label: "Conditions", count: counts.conditions },
          { id: "procedures", label: "Procedures", count: counts.procedures },
          { id: "immunizations", label: "Immunizations", count: counts.immunizations },
          { id: "examVitals", label: "Exam & Vitals", count: counts.examVitals },
        ]}
      />
      {active === "allergies" && <AllergiesPanel allergies={allergies} />}
      {active === "conditions" && <ConditionsPanel conditions={conditions} />}
      {active === "procedures" && <ProceduresPanel procedures={procedures} />}
      {active === "immunizations" && <ImmunizationsPanel immunizations={immunizations} />}
      {active === "examVitals" && <ExamVitalsPanel visits={examAndVitals} />}
    </section>
  );
}
